import { useState, useMemo } from "react";
import { Card, Radio, Alert, Spin, Table, Typography } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ReferenceLine,
  CartesianGrid,
  Cell,
  LabelList,
} from "recharts";
import "./Styles.css";

const { Text } = Typography;

// Mapping -> champs de Evaluation_PACE
const PACE_FIELD_BY_EVAL = {
  sppb: "__derived_sppb",
  "30sts": "chairTestCount",
  sts5x: "__derived_sts5x",
  equil: "scoreB",
  vitesseMarche: "vitesseDeMarche",
  frt: "frtDistance",
};

// ====== Config graphe / couleurs ======
const COLORS = {
  greenLight: "#CFE8B4",
  green: "#4CAF50",
  greenDark: "#2E7D32",
  yellow: "#FBC02D",
  orange: "#FB8C00",
  red: "#D32F2F",
  neutral: "#BDBDBD",
};

const EVAL_CONFIG = {
  sppb: { title: "Score SPPB (point)", unit: "pt", colorMode: "threshold" },
  "30sts": { title: "30 STS", unit: "rép.", colorMode: "fixed" },
  equil: { title: "Équilibre retenu (sec)", unit: "sec", colorMode: "fixed" },
  vitesseMarche: { title: "Vitesse de marche (m/s)", unit: "m/s", colorMode: "fixed" },
  frt: { title: "FRT (cm)", unit: "cm", colorMode: "fixed" },
  sts5x: { title: "STS5X", unit: "sec", colorMode: "fixed" },
};

function isNum(x) {
  const n = Number(x);
  return Number.isFinite(n);
}

function toNullableNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function toSupportCode(chairTestSupport) {
  if (chairTestSupport === true) return 0;
  if (chairTestSupport === false) return 1;
  const value = String(chairTestSupport ?? "").trim().toLowerCase();
  if (!value) return null;
  if (["with", "avec", "avec aide", "1", "true"].includes(value)) return 0;
  if (["without", "sans", "sans aide", "0", "false"].includes(value)) return 1;
  return null;
}

function safeWalkTimeFromSpeed(speed) {
  const v = toNullableNumber(speed);
  if (v == null || v <= 0) return null;
  return 4 / v;
}

function computeSppbBalanceTotal(paceEval) {
  const feet = toNullableNumber(paceEval?.balanceFeetTogether);
  const semi = toNullableNumber(paceEval?.balanceSemiTandem);
  const tandem = toNullableNumber(paceEval?.balanceTandem);
  const hasAnyValue = [feet, semi, tandem].some((v) => v != null);
  if (!hasAnyValue) return null;

  const pj = feet != null && feet >= 10 ? 1 : 0;
  const st = semi != null && semi >= 10 ? 1 : 0;
  let t = 0;
  if (tandem != null) {
    if (tandem >= 10) t = 2;
    else if (tandem >= 3) t = 1;
  }
  return pj + st + t;
}

function estimateSts5Time(chairCount, chairTestTime) {
  const direct = toNullableNumber(chairTestTime);
  if (direct != null && direct > 0) return direct;
  const count = toNullableNumber(chairCount);
  if (count == null || count <= 0) return null;
  return (5 * 30) / count;
}

function computeSppb5RepScore(sts5Time, supportCode) {
  const t = toNullableNumber(sts5Time);
  if (t == null) return null;
  if (supportCode === 0) return 0;
  if (t <= 0 || t > 60) return 0;
  if (t < 11.2) return 4;
  if (t < 13.7) return 3;
  if (t < 16.7) return 2;
  return 1;
}

function computeSppb4mScore(walkTimeSec) {
  const t = toNullableNumber(walkTimeSec);
  if (t == null) return null;
  if (t <= 0) return 0;
  if (t < 4.82) return 4;
  if (t < 6.21) return 3;
  if (t < 8.7) return 2;
  return 1;
}

function computeSppbTotalFromPace(paceEval) {
  if (!paceEval) return null;
  const supportCode = toSupportCode(paceEval?.chairTestSupport);
  const sts5Time = estimateSts5Time(
    paceEval?.chairTestCount,
    paceEval?.chairTestTime
  );
  const walkTime = safeWalkTimeFromSpeed(paceEval?.vitesseDeMarche);
  const balanceTotal = computeSppbBalanceTotal(paceEval);
  const score5Rep = computeSppb5RepScore(sts5Time, supportCode);
  const score4m = computeSppb4mScore(walkTime);
  if (
    !Number.isFinite(Number(balanceTotal)) ||
    !Number.isFinite(Number(score5Rep)) ||
    !Number.isFinite(Number(score4m))
  ) {
    return null;
  }
  return Number(balanceTotal) + Number(score5Rep) + Number(score4m);
}

function getPaceMetricValue(evalKey, paceEval, fallbackField) {
  if (!paceEval) return null;
  if (evalKey === "sppb") {
    return computeSppbTotalFromPace(paceEval);
  }
  if (evalKey === "sts5x") {
    return estimateSts5Time(paceEval?.chairTestCount, paceEval?.chairTestTime);
  }
  if (!fallbackField) return null;
  return paceEval?.[fallbackField] ?? null;
}

function sppbColorFromScore(score) {
  const s = Number(score);
  if (!Number.isFinite(s)) return COLORS.neutral;
  if (s <= 6) return COLORS.red;
  if (s <= 9) return COLORS.yellow;
  return COLORS.greenDark;
}

function vitesseColorFromValue(value) {
  const v = Number(value);
  if (!Number.isFinite(v)) return COLORS.neutral;
  if (v >= 1.2) return COLORS.greenDark;
  if (v >= 1.0) return COLORS.green;
  if (v >= 0.8) return COLORS.orange;
  return COLORS.red;
}

function formatValue(evalKey, value) {
  if (!Number.isFinite(value)) return "N/A";
  // Ajuste si tu veux des décimales différentes par test
  if (evalKey === "30sts" || evalKey === "equil" || evalKey === "sppb") {
    return String(Math.round(value));
  }
  if (evalKey === "vitesseMarche") return value.toFixed(2);
  if (evalKey === "frt") return value.toFixed(1);
  return value.toFixed(2);
}

function pickBestBalance(paceEval) {
  if (!paceEval) return null;
  const candidates = [
    { label: "Pieds unipodal", value: paceEval.balanceOneFooted },
    { label: "Pieds tandem", value: paceEval.balanceTandem },
    { label: "Pieds semi tandem", value: paceEval.balanceSemiTandem },
    { label: "Pieds joints", value: paceEval.balanceFeetTogether },
  ];
  for (const candidate of candidates) {
    const n = Number(candidate.value);
    if (Number.isFinite(n)) {
      return { label: candidate.label, value: n };
    }
  }
  return null;
}

function patientAgeYears(patient) {
  const d = dayjs(patient?.birthday);
  if (!d.isValid()) return null;
  return dayjs().diff(d, "year");
}

function normalizeGender(patient) {
  const raw = String(patient?.gender ?? "").trim().toUpperCase();
  if (raw === "F" || raw === "FEMALE" || raw === "FEMME") return "F";
  if (raw === "H" || raw === "M" || raw === "HOMME" || raw === "MALE") return "H";
  return null;
}

function getSts30RiskThreshold(age, gender) {
  if (!Number.isFinite(age) || age < 60 || age > 94) return null;
  if (!gender) return null;

  const ranges = [
    { min: 60, max: 64, F: 12, H: 14 },
    { min: 65, max: 69, F: 11, H: 12 },
    { min: 70, max: 74, F: 10, H: 12 },
    { min: 75, max: 79, F: 10, H: 12 },
    { min: 80, max: 84, F: 9, H: 10 },
    { min: 85, max: 89, F: 8, H: 8 },
    { min: 90, max: 94, F: 4, H: 7 },
  ];

  const row = ranges.find((r) => age >= r.min && age <= r.max);
  if (!row) return null;
  return row[gender] ?? null;
}

export const __suiviEvaluationsTestables = {
  isNum,
  toNullableNumber,
  toSupportCode,
  safeWalkTimeFromSpeed,
  computeSppbBalanceTotal,
  estimateSts5Time,
  computeSppb5RepScore,
  computeSppb4mScore,
  computeSppbTotalFromPace,
  getPaceMetricValue,
  sppbColorFromScore,
  vitesseColorFromValue,
  formatValue,
  pickBestBalance,
  patientAgeYears,
  normalizeGender,
  getSts30RiskThreshold,
};

export default function SuiviEvaluationsCard({
  patient,
  programType,
  paceT0T1,
  isLoading,
  isError,
}) {
  const { t } = useTranslation("Patients");
  const [activeEval, setActiveEval] = useState("vitesseMarche");
  const evalTabs = useMemo(
    () => [
      { key: "sppb", label: "SPPB" },
      { key: "30sts", label: "30 STS" },
      { key: "sts5x", label: "STS5X" },
      { key: "equil", label: t("stats_followup_tab_balance", "Balance") },
      { key: "vitesseMarche", label: t("stats_followup_tab_walk_speed", "Walk speed") },
      { key: "frt", label: "FRT" },
    ],
    [t]
  );

  const activeLabel = useMemo(
    () => evalTabs.find((e) => e.key === activeEval)?.label ?? "",
    [activeEval, evalTabs]
  );

  const t0Eval = paceT0T1?.t0 ?? null;
  const t1Eval = paceT0T1?.t1 ?? null;

  // Compatibilite YM-TEST2/YM-Test: backend peut renvoyer "pace" ou "Evaluation_PACE"
  const t0Pace = t0Eval?.pace ?? t0Eval?.Evaluation_PACE ?? null;
  const t1Pace = t1Eval?.pace ?? t1Eval?.Evaluation_PACE ?? null;

  const fieldName = PACE_FIELD_BY_EVAL[activeEval];
  const isEquilSingleMode = activeEval === "equil";
  const v0 = !isEquilSingleMode
    ? getPaceMetricValue(activeEval, t0Pace, fieldName)
    : null;
  const v1 = !isEquilSingleMode
    ? getPaceMetricValue(activeEval, t1Pace, fieldName)
    : null;
  const equilSource = t1Pace ?? t0Pace ?? null;
  const equilSingle = isEquilSingleMode ? pickBestBalance(equilSource) : null;
  const equilSingleValue = equilSingle?.value ?? null;
  const equilSingleLabel = equilSingle?.label ?? "N/A";

  const v0Num = isNum(v0) ? Number(v0) : null;
  const v1Num = isNum(v1) ? Number(v1) : null;

  const deltaNum =
    Number.isFinite(v0Num) && Number.isFinite(v1Num) ? v1Num - v0Num : null;
  const deltaText = Number.isFinite(deltaNum) ? deltaNum.toFixed(2) : null;

  const cfg = {
    ...(EVAL_CONFIG[activeEval] ?? { title: activeLabel, unit: "", colorMode: "fixed" }),
    title:
      t(`stats_followup_metric_${activeEval}`, {
        defaultValue: EVAL_CONFIG[activeEval]?.title || activeLabel,
      }) || activeLabel,
  };
  const age = patientAgeYears(patient);
  const gender = normalizeGender(patient);
  const sts30RiskThreshold = activeEval === "30sts" ? getSts30RiskThreshold(age, gender) : null;
  const sts30CurrentValue =
    Number.isFinite(v1Num) ? v1Num : Number.isFinite(v0Num) ? v0Num : null;
  const sts30RiskStatus =
    Number.isFinite(sts30RiskThreshold) && Number.isFinite(sts30CurrentValue)
      ? sts30CurrentValue < sts30RiskThreshold
        ? "À risque"
        : "Non à risque"
      : null;

  const columns = useMemo(
    () =>
      isEquilSingleMode
        ? [
            {
              title: t("stats_followup_selected_balance", "Selected balance"),
              dataIndex: "equilLabel",
              key: "equilLabel",
              align: "center",
            },
            {
              title: t("stats_followup_value_seconds", "Value (sec)"),
              dataIndex: "equilValue",
              key: "equilValue",
              align: "center",
            },
          ]
        : [
            { title: t("stats_followup_eval_t0", "Eval. T0"), dataIndex: "t0", key: "t0" },
            { title: t("stats_followup_eval_t1_12w", "Eval. T1 (12w)"), dataIndex: "t1", key: "t1" },
            { title: t("stats_followup_delta", "Delta(Δ)"), dataIndex: "delta", key: "delta" },
          ],
    [isEquilSingleMode, t]
  );

  const dataSource = useMemo(
    () =>
      isEquilSingleMode
        ? [
            {
              key: "row",
              equilLabel: equilSingleLabel,
              equilValue: Number.isFinite(equilSingleValue)
                ? formatValue(activeEval, equilSingleValue)
                : "N/A",
            },
          ]
        : [
            {
              key: "row",
              t0: Number.isFinite(v0Num) ? formatValue(activeEval, v0Num) : "N/A",
              t1: Number.isFinite(v1Num) ? formatValue(activeEval, v1Num) : "N/A",
              delta: deltaText ?? "N/A",
            },
          ],
    [isEquilSingleMode, equilSingleLabel, equilSingleValue, v0Num, v1Num, deltaText, activeEval]
  );
const chartData = useMemo(() => {
  if (isEquilSingleMode) {
    if (!Number.isFinite(equilSingleValue)) return [];
    return [
      {
        key: "EQUIL",
        label: t("stats_followup_tab_balance", "Balance"),
        value: equilSingleValue,
        labelText: formatValue(activeEval, equilSingleValue),
      },
    ];
  }

  return [
    { key: "T0", label: "T0", value: v0Num },
    { key: "T1", label: "T1", value: v1Num },
    { key: "DELTA", label: "Δ", value: deltaNum },
  ]
    .filter((d) => Number.isFinite(d.value))
    .map((d) => ({
      ...d,
      labelText:
        d.key === "DELTA"
          ? Number(d.value).toFixed(2)
          : formatValue(activeEval, Number(d.value)),
    }));
}, [isEquilSingleMode, equilSingleValue, v0Num, v1Num, deltaNum, activeEval, t]);

const yDomain = useMemo(() => {
  const vals = chartData.map((d) => d.value).filter(Number.isFinite);
  if (vals.length === 0) return [0, "auto"];

  if (activeEval === "sppb") {
    const min = Math.min(...vals);
    return [Math.min(0, min), 12];
  }

  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const fixedVitesseThreshold = 0.8;
  const maxWithThreshold =
    activeEval === "30sts" && Number.isFinite(sts30RiskThreshold)
      ? Math.max(max, sts30RiskThreshold)
      : activeEval === "vitesseMarche"
        ? Math.max(max, fixedVitesseThreshold)
      : max;

  // Toujours partir de 0 si tout est positif
  // Descendre sous 0 si Δ est négatif
  return [Math.min(0, min), Math.max(0, maxWithThreshold)];
}, [chartData, activeEval, sts30RiskThreshold]);
  

   



  function barColor(entry) {
    // Δ : positif = vert foncé ; négatif = rouge ; 0 = vert foncé ; N/A = gris
    if (entry.key === "DELTA") {
      if (!Number.isFinite(entry.value)) return COLORS.neutral;
      if (entry.value < 0) return COLORS.red;
      return COLORS.greenLight;
    }

    if (entry.key === "EQUIL") {
      return COLORS.greenDark;
    }

    // SPPB : T0/T1 colorés selon score
    if (activeEval === "sppb") {
      return sppbColorFromScore(entry.value);
    }

    // Vitesse de marche: couleur selon seuils cliniques
    if (activeEval === "vitesseMarche") {
      if (entry.key === "DELTA") {
        if (!Number.isFinite(entry.value)) return COLORS.neutral;
        return entry.value < 0 ? COLORS.red : COLORS.greenDark;
      }
      return vitesseColorFromValue(entry.value);
    }

    // Tous les autres : couleurs fixes style 30 STS
    return entry.key === "T0" ? COLORS.greenLight : COLORS.greenDark;
  }

  if (programType !== "PACE") {
    return (
      <div>
        <h2 className="section-header">{t("stats_section_followup", "Evaluation follow-up")}</h2>
        <Card>
          <Alert
            type="info"
            showIcon
            message={t("stats_followup_only_pace", "Evaluation follow-up: PACE only for now.")}
          />
        </Card>
      </div>
    );
  }

  return (
    <div>
      <h2 className="section-header">{t("stats_section_followup", "Evaluation follow-up")}</h2>

      <Card>
        <Radio.Group
          className="chip-radio chip-radio--orange chip-radio--center chip-radio--eval-patient"
          value={activeEval}
          onChange={(e) => setActiveEval(e.target.value)}
        >
          {evalTabs.map((e) => (
            <Radio.Button key={e.key} value={e.key}>
              {e.label}
            </Radio.Button>
          ))}
        </Radio.Group>

        <div className="eval-content" style={{ marginTop: 12 }}>
          {isLoading ? (
            <Spin />
          ) : isError ? (
            <Alert
              type="error"
              showIcon
              message={t("stats_error_loading_pace_t0_t1", "Error: /patient/:id/pace/t0t1")}
            />
          ) : !t0Eval ? (
            <Alert
              type="info"
              showIcon
              message={t("stats_no_t0_evaluation_found", "No T0 evaluation found")}
            />
          ) : !fieldName ? (
            <Alert
              type="warning"
              showIcon
              message={t("stats_followup_missing_mapping", {
                test: activeLabel,
                defaultValue: "Test {{test}} not mapped yet (missing mapping).",
              })}
              description={
                <>
                  <div>{t("stats_followup_available_keys", "Available keys in t0.pace:")}</div>
                  <div style={{ fontFamily: "monospace", marginTop: 6 }}>
                    {Object.keys(t0Pace ?? {}).join(", ") || t("stats_followup_no_key", "(none)")}
                  </div>
                </>
              }
            />
          ) : (
            <>{/* ====== TABLE ====== */}
              <Table
                size="small"
                pagination={false}
                columns={columns}
                dataSource={dataSource}
              />
              {/* ====== GRAPHE ====== */}
              <div style={{ marginBottom: 10 }}>
                <Text strong>{cfg.title}</Text>
              </div>

              {chartData.length === 0 ? (
                <Alert
                  type="info"
                  showIcon
                  message={t("stats_followup_no_numeric_data", "No numeric data to display.")}
                />
              ) : (
                <div
  style={{
    width: "100%",
    maxWidth: 650,
    height: 260,
    margin: "0 auto 14px auto",
  }}
>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="label" />
                      <YAxis domain={yDomain} />
                      {activeEval === "30sts" && Number.isFinite(sts30RiskThreshold) && (
                        <ReferenceLine
                          y={sts30RiskThreshold}
                          stroke={COLORS.red}
                          strokeDasharray="6 4"
                          ifOverflow="extendDomain"
                        />
                      )}
                      {activeEval === "vitesseMarche" && (
                        <ReferenceLine
                          y={0.8}
                          stroke={COLORS.red}
                          strokeDasharray="6 4"
                          ifOverflow="extendDomain"
                        />
                      )}
                      
                      <Bar dataKey="value" isAnimationActive={false} barSize={55}>
                        {chartData.map((entry) => (
                          <Cell key={entry.key} fill={barColor(entry)} />
                        ))}
                        <LabelList dataKey="labelText" position="insideBottom" fill="#ffffff" />
                        
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Mini-légende : SPPB (seuils) vs autres (T0/T1/Δ) */}
              {activeEval === "sppb" ? (
                <div style={{ display: "flex", gap: 16, alignItems: "center", marginBottom: 12 }}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <span style={{ width: 10, height: 10, borderRadius: 6, background: COLORS.red, display: "inline-block" }} />
                    <span>0–6</span>
                  </span>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <span style={{ width: 10, height: 10, borderRadius: 6, background: COLORS.yellow, display: "inline-block" }} />
                    <span>7–9</span>
                  </span>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <span style={{ width: 10, height: 10, borderRadius: 6, background: COLORS.greenDark, display: "inline-block" }} />
                    <span>10–12</span>
                  </span>
                  <span style={{ marginLeft: "auto" }}>
                    <span style={{ width: 10, height: 10, borderRadius: 6, background: COLORS.greenDark, display: "inline-block", marginRight: 6 }} />
                    Δ positif
                    <span style={{ width: 10, height: 10, borderRadius: 6, background: COLORS.red, display: "inline-block", margin: "0 6px 0 10px" }} />
                    Δ négatif
                  </span>
                </div>
              ) : activeEval === "equil" ? (
                <div style={{ display: "flex", gap: 16, alignItems: "center", marginBottom: 12 }}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <span style={{ width: 10, height: 10, borderRadius: 6, background: COLORS.greenDark, display: "inline-block" }} />
                    <span>
                      {Number.isFinite(equilSingleValue)
                        ? `${equilSingleLabel} : ${formatValue(activeEval, equilSingleValue)} sec`
                        : "Aucune valeur d'équilibre"}
                    </span>
                  </span>
                </div>
              ) : activeEval === "vitesseMarche" ? (
                <div style={{ display: "flex", gap: 16, alignItems: "center", marginBottom: 12, flexWrap: "wrap" }}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <span style={{ width: 10, height: 10, borderRadius: 6, background: COLORS.greenDark, display: "inline-block" }} />
                    <span>≥ 1,2 m/s</span>
                  </span>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <span style={{ width: 10, height: 10, borderRadius: 6, background: COLORS.green, display: "inline-block" }} />
                    <span>1 à &lt; 1,2 m/s</span>
                  </span>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <span style={{ width: 10, height: 10, borderRadius: 6, background: COLORS.orange, display: "inline-block" }} />
                    <span>0,8 à &lt; 1 m/s</span>
                  </span>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <span style={{ width: 10, height: 10, borderRadius: 6, background: COLORS.red, display: "inline-block" }} />
                    <span>&lt; 0,8 m/s</span>
                  </span>
                </div>
              ) : activeEval === "30sts" ? (
                <div style={{ display: "flex", gap: 16, alignItems: "center", marginBottom: 12, flexWrap: "wrap" }}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <span style={{ width: 10, height: 10, borderRadius: 6, background: COLORS.greenLight, display: "inline-block" }} />
                    <span>T0</span>
                  </span>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <span style={{ width: 10, height: 10, borderRadius: 6, background: COLORS.greenDark, display: "inline-block" }} />
                    <span>T1</span>
                  </span>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <span style={{ width: 14, height: 0, borderTop: `2px dashed ${COLORS.red}`, display: "inline-block" }} />
                    <span>
                      {Number.isFinite(sts30RiskThreshold)
                        ? `Seuil à risque: ${sts30RiskThreshold}`
                        : "Seuil à risque indisponible"}
                    </span>
                  </span>
                  {sts30RiskStatus && (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                      <span
                        style={{
                          width: 10,
                          height: 10,
                          borderRadius: 6,
                          background: sts30RiskStatus === "À risque" ? COLORS.red : COLORS.greenDark,
                          display: "inline-block",
                        }}
                      />
                      <span>{sts30RiskStatus}</span>
                    </span>
                  )}
                </div>
              ) : (
                <div style={{ display: "flex", gap: 16, alignItems: "center", marginBottom: 12 }}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <span style={{ width: 10, height: 10, borderRadius: 6, background: COLORS.greenLight, display: "inline-block" }} />
                    <span>T0</span>
                  </span>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <span style={{ width: 10, height: 10, borderRadius: 6, background: COLORS.greenDark, display: "inline-block" }} />
                    <span>T1</span>
                  </span>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <span style={{ width: 10, height: 10, borderRadius: 6, background: COLORS.greenDark, display: "inline-block" }} />
                    <span>Δ positif</span>
                  </span>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <span style={{ width: 10, height: 10, borderRadius: 6, background: COLORS.red, display: "inline-block" }} />
                    <span>Δ négatif</span>
                  </span>
                </div>
              )}

              
            </>
          )}
        </div>
      </Card>
    </div>
  );
}
